/**
 * Store Settings & Shipping Configuration Manager.
 * Handles synchronization between Supabase public.store_settings table,
 * browser local cache, and reactive React UI components.
 */

import { useState, useEffect } from "react";
import { siteConfig } from "@/config/site";
import { supabase } from "@/lib/supabase";
import type { PricingConfig } from "@/lib/pricing-calculator";

export interface StoreShippingSettings {
  standardShippingFee: number;
  expressShippingFee: number;
  freeShippingThreshold: number;
  standardDays: string;
  expressDays: string;
}

export interface StoreTaxSettings {
  pricesIncludeGst: boolean;
  gstRate: number; // e.g. 0.18 for 18% GST
}

export const DEFAULT_SHIPPING_SETTINGS: StoreShippingSettings = {
  standardShippingFee: siteConfig.pricing.standardShippingFee,
  expressShippingFee: siteConfig.pricing.expressShippingFee,
  freeShippingThreshold: siteConfig.pricing.freeShippingThreshold,
  standardDays: siteConfig.shipping.standardDays,
  expressDays: siteConfig.shipping.expressDays,
};

export const DEFAULT_TAX_SETTINGS: StoreTaxSettings = {
  pricesIncludeGst: siteConfig.pricing.pricesIncludeGst ?? true,
  gstRate: siteConfig.pricing.gstRate ?? 0.18,
};

export const SHIPPING_SETTINGS_STORAGE_KEY = "verdant_shipping_settings_v2";
export const STORE_SETTINGS_KEY = "verdant_store_settings_v1";
export const SETTINGS_KEY = STORE_SETTINGS_KEY;
export const SHIPPING_SETTINGS_EVENT = "store_shipping_settings_updated";
export const TAX_SETTINGS_STORAGE_KEY = "verdant_tax_settings_v1";
export const TAX_SETTINGS_EVENT = "store_tax_settings_updated";

/**
 * Retrieve cached shipping settings synchronously from localStorage,
 * falling back to siteConfig defaults.
 */
export function getStoredShippingSettings(): StoreShippingSettings {
  if (typeof window === "undefined") {
    return { ...DEFAULT_SHIPPING_SETTINGS };
  }

  try {
    const raw = localStorage.getItem(SHIPPING_SETTINGS_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      const rawStandardFee =
        parsed.standardShippingFee === 499
          ? DEFAULT_SHIPPING_SETTINGS.standardShippingFee
          : parsed.standardShippingFee;
      const settings = {
        standardShippingFee:
          typeof rawStandardFee === "number"
            ? rawStandardFee
            : DEFAULT_SHIPPING_SETTINGS.standardShippingFee,
        expressShippingFee:
          typeof parsed.expressShippingFee === "number"
            ? parsed.expressShippingFee
            : DEFAULT_SHIPPING_SETTINGS.expressShippingFee,
        freeShippingThreshold:
          typeof parsed.freeShippingThreshold === "number"
            ? parsed.freeShippingThreshold
            : DEFAULT_SHIPPING_SETTINGS.freeShippingThreshold,
        standardDays: parsed.standardDays || DEFAULT_SHIPPING_SETTINGS.standardDays,
        expressDays: parsed.expressDays || DEFAULT_SHIPPING_SETTINGS.expressDays,
      };
      if (parsed.standardShippingFee === 499) {
        try {
          localStorage.setItem(SHIPPING_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
        } catch {}
      }
      return settings;
    }

    // Check legacy store settings key as secondary fallback
    const legacyRaw = localStorage.getItem(STORE_SETTINGS_KEY);
    if (legacyRaw) {
      const legacy = JSON.parse(legacyRaw);
      const rawStandardFee =
        legacy.standardShippingFee === 499
          ? DEFAULT_SHIPPING_SETTINGS.standardShippingFee
          : legacy.standardShippingFee;
      if (rawStandardFee !== undefined || legacy.freeShippingThreshold !== undefined) {
        return {
          standardShippingFee:
            typeof rawStandardFee === "number"
              ? rawStandardFee
              : DEFAULT_SHIPPING_SETTINGS.standardShippingFee,
          expressShippingFee:
            typeof legacy.expressShippingFee === "number"
              ? legacy.expressShippingFee
              : DEFAULT_SHIPPING_SETTINGS.expressShippingFee,
          freeShippingThreshold:
            typeof legacy.freeShippingThreshold === "number"
              ? legacy.freeShippingThreshold
              : DEFAULT_SHIPPING_SETTINGS.freeShippingThreshold,
          standardDays: legacy.standardDays || DEFAULT_SHIPPING_SETTINGS.standardDays,
          expressDays: legacy.expressDays || DEFAULT_SHIPPING_SETTINGS.expressDays,
        };
      }
    }
  } catch (err) {
    console.warn("Failed to read shipping settings from localStorage:", err);
  }

  return { ...DEFAULT_SHIPPING_SETTINGS };
}

/**
 * Fetch latest shipping settings from Supabase public.store_settings table.
 * Automatically updates localStorage and notifies any active subscribers.
 */
export async function fetchShippingSettingsFromSupabase(): Promise<StoreShippingSettings> {
  const current = getStoredShippingSettings();

  try {
    const { data, error } = await supabase
      .from("store_settings")
      .select("key, value")
      .eq("key", "shipping")
      .maybeSingle();

    if (!error && data && data.value) {
      const val = data.value;
      const updated: StoreShippingSettings = {
        standardShippingFee:
          val.standardShippingFee === 499
            ? 60
            : typeof val.standardShippingFee === "number"
            ? val.standardShippingFee
            : current.standardShippingFee,
        expressShippingFee:
          typeof val.expressShippingFee === "number"
            ? val.expressShippingFee
            : current.expressShippingFee,
        freeShippingThreshold:
          typeof val.freeShippingThreshold === "number"
            ? val.freeShippingThreshold
            : current.freeShippingThreshold,
        standardDays: val.standardDays || val.estimatedDays || current.standardDays,
        expressDays: val.expressDays || current.expressDays,
      };

      if (typeof window !== "undefined") {
        try {
          localStorage.setItem(SHIPPING_SETTINGS_STORAGE_KEY, JSON.stringify(updated));
          window.dispatchEvent(
            new CustomEvent(SHIPPING_SETTINGS_EVENT, { detail: updated })
          );
        } catch {}
      }

      return updated;
    }
  } catch (err) {
    console.warn("Could not query Supabase store_settings:", err);
  }

  return current;
}

/**
 * Persist shipping settings authoritatively to Supabase store_settings,
 * sync to localStorage, and broadcast updates to all listening components.
 */
export async function saveShippingSettingsToSupabase(
  newSettings: Partial<StoreShippingSettings>
): Promise<StoreShippingSettings> {
  const current = getStoredShippingSettings();
  const merged: StoreShippingSettings = {
    ...current,
    ...newSettings,
  };

  // 1. Cache immediately in localStorage
  if (typeof window !== "undefined") {
    try {
      localStorage.setItem(SHIPPING_SETTINGS_STORAGE_KEY, JSON.stringify(merged));
      window.dispatchEvent(
        new CustomEvent(SHIPPING_SETTINGS_EVENT, { detail: merged })
      );
    } catch {}
  }

  // 2. Persist to Supabase
  const { error } = await supabase.from("store_settings").upsert([
    {
      key: "shipping",
      value: {
        standardShippingFee: merged.standardShippingFee,
        expressShippingFee: merged.expressShippingFee,
        freeShippingThreshold: merged.freeShippingThreshold,
        standardDays: merged.standardDays,
        expressDays: merged.expressDays,
        estimatedDays: merged.standardDays,
      },
      updated_at: new Date().toISOString(),
    },
  ]);

  if (error) {
    console.error("Supabase shipping settings save error:", error);
    throw new Error(error.message || "Failed to persist shipping settings to database.");
  }

  return merged;
}

let globalShippingChannel: ReturnType<typeof supabase.channel> | null = null;
let globalShippingSubscribers = 0;

function subscribeShippingRealtime(): () => void {
  if (typeof window === "undefined") return () => {};
  globalShippingSubscribers++;
  if (globalShippingSubscribers === 1) {
    try {
      globalShippingChannel = supabase
        .channel(`store_settings_shipping_${Date.now()}`)
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "store_settings", filter: "key=eq.shipping" },
          () => {
            fetchShippingSettingsFromSupabase();
          }
        )
        .subscribe();
    } catch (err) {
      console.warn("Could not subscribe to shipping settings realtime:", err);
    }
  }
  return () => {
    globalShippingSubscribers--;
    if (globalShippingSubscribers <= 0) {
      globalShippingSubscribers = 0;
      if (globalShippingChannel) {
        supabase.removeChannel(globalShippingChannel);
        globalShippingChannel = null;
      }
    }
  };
}

/**
 * Custom React Hook to consume live shipping settings in any component.
 * Automatically synchronizes with Supabase and reacts to admin changes in real time.
 */
export function useStoreShippingSettings(): StoreShippingSettings {
  const [settings, setSettings] = useState<StoreShippingSettings>(() =>
    getStoredShippingSettings()
  );

  useEffect(() => {
    // 1. Fetch latest from database on mount
    fetchShippingSettingsFromSupabase().then((latest) => {
      setSettings(latest);
    });

    // 2. Listen for real-time local updates across components/tabs
    const handleUpdate = (e: Event) => {
      if (e instanceof CustomEvent && e.detail) {
        setSettings(e.detail);
      } else {
        setSettings(getStoredShippingSettings());
      }
    };

    window.addEventListener(SHIPPING_SETTINGS_EVENT, handleUpdate);
    window.addEventListener("storage", handleUpdate);

    // 3. Module-scoped ref-counted Supabase Realtime channel
    const unsubscribeRealtime = subscribeShippingRealtime();

    return () => {
      window.removeEventListener(SHIPPING_SETTINGS_EVENT, handleUpdate);
      window.removeEventListener("storage", handleUpdate);
      unsubscribeRealtime();
    };
  }, []);

  return settings;
}

/**
 * Retrieve cached tax settings synchronously from localStorage, falling back to defaults.
 */
export function getStoredTaxSettings(): StoreTaxSettings {
  if (typeof window === "undefined") {
    return { ...DEFAULT_TAX_SETTINGS };
  }

  try {
    const raw = localStorage.getItem(TAX_SETTINGS_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        pricesIncludeGst:
          typeof parsed.pricesIncludeGst === "boolean"
            ? parsed.pricesIncludeGst
            : DEFAULT_TAX_SETTINGS.pricesIncludeGst,
        gstRate:
          typeof parsed.gstRate === "number"
            ? parsed.gstRate
            : DEFAULT_TAX_SETTINGS.gstRate,
      };
    }
  } catch (err) {
    console.warn("Failed to read tax settings from localStorage:", err);
  }

  return { ...DEFAULT_TAX_SETTINGS };
}

/**
 * Fetch latest tax settings from Supabase public.store_settings table.
 */
export async function fetchTaxSettingsFromSupabase(): Promise<StoreTaxSettings> {
  const current = getStoredTaxSettings();

  try {
    const { data, error } = await supabase
      .from("store_settings")
      .select("key, value")
      .eq("key", "tax")
      .maybeSingle();

    if (!error && data && data.value) {
      const val = data.value;
      const updated: StoreTaxSettings = {
        pricesIncludeGst:
          typeof val.pricesIncludeGst === "boolean"
            ? val.pricesIncludeGst
            : current.pricesIncludeGst,
        gstRate:
          typeof val.gstRate === "number"
            ? val.gstRate
            : current.gstRate,
      };

      if (typeof window !== "undefined") {
        try {
          localStorage.setItem(TAX_SETTINGS_STORAGE_KEY, JSON.stringify(updated));
          window.dispatchEvent(
            new CustomEvent(TAX_SETTINGS_EVENT, { detail: updated })
          );
        } catch {}
      }

      return updated;
    }
  } catch (err) {
    console.warn("Could not query Supabase store_settings for tax:", err);
  }

  return current;
}

/**
 * Persist tax settings to Supabase store_settings, sync to localStorage, and broadcast.
 */
export async function saveTaxSettingsToSupabase(
  newSettings: Partial<StoreTaxSettings>
): Promise<StoreTaxSettings> {
  const current = getStoredTaxSettings();
  const merged: StoreTaxSettings = {
    ...current,
    ...newSettings,
  };

  if (typeof window !== "undefined") {
    try {
      localStorage.setItem(TAX_SETTINGS_STORAGE_KEY, JSON.stringify(merged));
      window.dispatchEvent(
        new CustomEvent(TAX_SETTINGS_EVENT, { detail: merged })
      );
    } catch {}
  }

  const { error } = await supabase.from("store_settings").upsert([
    {
      key: "tax",
      value: {
        pricesIncludeGst: merged.pricesIncludeGst,
        gstRate: merged.gstRate,
      },
      updated_at: new Date().toISOString(),
    },
  ]);

  if (error) {
    console.error("Supabase tax settings save error:", error);
    throw new Error(error.message || "Failed to persist tax settings to database.");
  }

  return merged;
}

/**
 * Custom React Hook to consume live tax settings in any component.
 */
export function useStoreTaxSettings(): StoreTaxSettings {
  const [settings, setSettings] = useState<StoreTaxSettings>(() =>
    getStoredTaxSettings()
  );

  useEffect(() => {
    fetchTaxSettingsFromSupabase().then((latest) => {
      setSettings(latest);
    });

    const handleUpdate = (e: Event) => {
      if (e instanceof CustomEvent && e.detail) {
        setSettings(e.detail);
      } else {
        setSettings(getStoredTaxSettings());
      }
    };

    window.addEventListener(TAX_SETTINGS_EVENT, handleUpdate);
    window.addEventListener("storage", handleUpdate);

    return () => {
      window.removeEventListener(TAX_SETTINGS_EVENT, handleUpdate);
      window.removeEventListener("storage", handleUpdate);
    };
  }, []);

  return settings;
}

/**
 * Helper to get a dynamic PricingConfig using current stored shipping & tax settings.
 */
export function getLivePricingConfig(
  customSettings?: Partial<StoreShippingSettings & StoreTaxSettings>
): Partial<PricingConfig> {
  const baseShipping = getStoredShippingSettings();
  const baseTax = getStoredTaxSettings();
  return {
    standardShippingFee: customSettings?.standardShippingFee ?? baseShipping.standardShippingFee,
    expressShippingFee: customSettings?.expressShippingFee ?? baseShipping.expressShippingFee,
    freeShippingThreshold: customSettings?.freeShippingThreshold ?? baseShipping.freeShippingThreshold,
    gstRate: customSettings?.gstRate ?? baseTax.gstRate,
    pricesIncludeGst: customSettings?.pricesIncludeGst ?? baseTax.pricesIncludeGst,
  };
}
